import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  useTheme,
  Breadcrumbs,
  Link,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Product, createCheckoutSession, stripePromise } from '../services/stripe';
import { useCart } from '../context/CartContext';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';
import { API_BASE_URL } from '../config/api';

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { addToCart } = useCart();
  const { addToRecentlyViewed } = useRecentlyViewed();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE_URL}/products/${productId}`);
        if (!response.ok) {
          throw new Error('Product not found');
        }
        
        const data = await response.json();
        console.log('Product data:', data); // Add logging to debug
        setProduct(data);
        // Ensure we have an array of images
        if (data.images && Array.isArray(data.images) && data.images.length > 0) {
          setCurrentImageIndex(0);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch product');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product);
    }
  }, [product, addToRecentlyViewed]);

  const handlePrevImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : product.images.length - 1
      );
    }
  };

  const handleNextImage = () => {
    if (product?.images) {
      setCurrentImageIndex((prev) => 
        prev < product.images.length - 1 ? prev + 1 : 0
      );
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    
    try {
      setIsLoading(true);
      const sessionId = await createCheckoutSession(product.id);
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Error during checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to process checkout');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error" gutterBottom>
          {error || 'Product not found'}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{
            mb: 4,
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: 'transparent',
              color: theme.palette.primary.main,
            },
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Breadcrumb Navigation */}
          <Breadcrumbs 
            aria-label="breadcrumb"
            sx={{ 
              mb: 2,
              '& .MuiLink-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  color: 'white',
                },
              },
              '& .MuiTypography-root': {
                color: 'white',
              },
            }}
          >
            <Link
              component="button"
              onClick={() => navigate('/')}
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Home
            </Link>
            <Link
              component="button"
              onClick={() => navigate('/products')}
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Products
            </Link>
            <Typography>{product.name}</Typography>
          </Breadcrumbs>

          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            {product.name}
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'primary.light',
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            ${product.price.toFixed(2)}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Product Images */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: 'background.paper',
                '&:hover .image-controls': {
                  opacity: 1,
                },
              }}
            >
              <Box
                component="img"
                src={product.images?.[currentImageIndex] || product.image}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  maxHeight: 500,
                  objectFit: 'contain',
                  p: 2,
                }}
              />
              {product.images && product.images.length > 1 && (
                <Box
                  className="image-controls"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      mx: 1,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      mx: 1,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              )}
            </Paper>
            
            {/* Thumbnail Images */}
            {product.images && product.images.length > 1 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                {product.images.map((img, index) => (
                  <Paper
                    key={index}
                    elevation={2}
                    sx={{
                      p: 0.5,
                      borderRadius: 1,
                      border: index === currentImageIndex ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <Box
                      component="img"
                      src={img}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 0.5,
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            )}
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                bgcolor: 'background.paper',
                height: '100%',
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>

              {/* Additional Product Details */}
              {product.metadata && Object.entries(product.metadata).map(([key, value]) => {
                if (key === 'images' || key === 'category') return null;
                return (
                  <Box key={key} sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                    <Typography variant="body1">{String(value)}</Typography>
                  </Box>
                );
              })}

              {/* Action Buttons */}
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => addToCart({
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    image: product.image,
                    quantity: 1
                  })}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    flex: 1,
                  }}
                >
                  Add to Cart
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBuyNow}
                  disabled={isLoading}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      bgcolor: 'transparent',
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    flex: 1,
                  }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Buy Now'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductDetail;
